import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Input,
  Dropdown,
  Combobox,
  Option,
  Field,
  Body1,
  makeStyles,
  tokens
} from "@fluentui/react-components";
import type { AdminUser, Department } from "../pages/AdminUsers";

interface Props {
  open: boolean;
  /** Active users only; used as the manager picker source. */
  activeUsers: AdminUser[];
  departments: Department[];
  onClose: () => void;
  onCreated: (displayName: string) => void;
}

const useStyles = makeStyles({
  form: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
    paddingTop: tokens.spacingVerticalS
  },
  row: {
    display: "flex",
    gap: tokens.spacingHorizontalM
  },
  cell: { flex: 1 },
  hint: { color: tokens.colorNeutralForeground3 },
  error: { color: tokens.colorPaletteRedForeground1 }
});

export default function AddUserDialog({ open, activeUsers, departments, onClose, onCreated }: Props) {
  const styles = useStyles();
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [departmentId, setDepartmentId] = useState<string>("");
  const [managerId, setManagerId] = useState<string>("");
  const [hireDate, setHireDate] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Reset on every open so a previous attempt doesn't leak in.
    if (open) {
      setEmail("");
      setDisplayName("");
      setJobTitle("");
      setDepartmentId("");
      setManagerId("");
      setHireDate("");
      setSubmitting(false);
      setError(null);
    }
  }, [open]);

  const selectedDepartment = useMemo(
    () => departments.find((d) => d.id === departmentId) ?? null,
    [departments, departmentId]
  );
  const selectedManager = useMemo(
    () => activeUsers.find((u) => u.id === managerId) ?? null,
    [activeUsers, managerId]
  );

  const canSubmit =
    !submitting &&
    email.trim().length > 0 &&
    displayName.trim().length > 0;

  async function submit() {
    setSubmitting(true);
    setError(null);
    try {
      const r = await fetch("/api/manage-users", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim(),
          displayName: displayName.trim(),
          jobTitle: jobTitle.trim() || null,
          departmentId: departmentId || null,
          managerId: managerId || null,
          hireDate: hireDate || null
        })
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.error ?? `HTTP ${r.status}`);
      }
      const created = displayName.trim();
      onCreated(created);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(_, d) => {
        if (!d.open && !submitting) onClose();
      }}
    >
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Add user</DialogTitle>
          <DialogContent>
            <div className={styles.form}>
              <Field label="Email" required>
                <Input
                  type="email"
                  placeholder="first.last@adquire.com"
                  value={email}
                  onChange={(_, d) => setEmail(d.value)}
                />
              </Field>
              <Field label="Display name" required>
                <Input
                  placeholder="First Last"
                  value={displayName}
                  onChange={(_, d) => setDisplayName(d.value)}
                />
              </Field>
              <Field label="Job title">
                <Input value={jobTitle} onChange={(_, d) => setJobTitle(d.value)} />
              </Field>
              <div className={styles.row}>
                <div className={styles.cell}>
                  <Field label="Department">
                    <Dropdown
                      placeholder="None"
                      value={selectedDepartment?.name ?? ""}
                      selectedOptions={departmentId ? [departmentId] : []}
                      onOptionSelect={(_, d) => setDepartmentId(String(d.optionValue ?? ""))}
                    >
                      <Option value="" text="None">— None —</Option>
                      {departments.map((d) => (
                        <Option key={d.id} value={d.id} text={d.name}>
                          {d.name}
                        </Option>
                      ))}
                    </Dropdown>
                  </Field>
                </div>
                <div className={styles.cell}>
                  <Field label="Hire date">
                    <Input
                      type="date"
                      value={hireDate}
                      onChange={(_, d) => setHireDate(d.value)}
                    />
                  </Field>
                </div>
              </div>
              <Field label="Manager">
                <Combobox
                  placeholder="None — top of hierarchy"
                  value={selectedManager?.displayName ?? ""}
                  selectedOptions={managerId ? [managerId] : []}
                  onOptionSelect={(_, d) => setManagerId(String(d.optionValue ?? ""))}
                >
                  <Option value="" text="None">— None —</Option>
                  {activeUsers.map((u) => (
                    <Option key={u.id} value={u.id} text={u.displayName}>
                      {u.displayName} — {u.email}
                    </Option>
                  ))}
                </Combobox>
              </Field>

              <Body1 className={styles.hint}>
                The new user will be active and able to sign in immediately with their AdQuire Microsoft 365 account.
                Set leave-type allocations afterwards via <strong>Team → Adjust allocations</strong>.
              </Body1>

              {error && <Body1 className={styles.error}>{error}</Body1>}
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button appearance="primary" onClick={submit} disabled={!canSubmit}>
              {submitting ? "Adding…" : "Add user"}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
